import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { Sparkles, Send, Loader2, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Coach IA — embedded pedagogical chat for a course step.
 * Anonymous users can ask questions (stateless); logged-in users get a
 * per-session persisted thread. The coach is anchored on the course's factual
 * content and refuses to reveal quiz/challenge/exam answer keys.
 */
export default function CoachIA({ slug, step, loggedIn }) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);           // [{q, a, ts}]
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [turns, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = q.trim();
    if (!text || sending) return;
    setSending(true);
    setTurns((t2) => [...t2, { q: text, a: null, pending: true }]);
    setQ("");
    try {
      const { data } = await api.post(`/academy/courses/${slug}/coach`, {
        question: text,
        step,
        lang,
        session_id: sessionId,
      });
      if (!sessionId && data.session_id) setSessionId(data.session_id);
      setTurns((prev) => {
        const copy = [...prev];
        const idx = copy.length - 1;
        if (idx >= 0) copy[idx] = { q: text, a: data.answer, ts: new Date().toISOString() };
        return copy;
      });
    } catch (err) {
      setTurns((prev) => {
        const copy = [...prev];
        const idx = copy.length - 1;
        if (idx >= 0) copy[idx] = { q: text, a: null, error: true };
        return copy;
      });
      toast.error(err?.response?.data?.detail || t("coach.error"));
    } finally {
      setSending(false);
    }
  };

  const suggestions = suggestionsFor(step, t);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="coach-ia-open"
        className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-[#E5276A]/40 bg-[#FCE4ED]/50 text-[#E5276A] text-sm font-bold hover:bg-[#FCE4ED] hover:border-[#E5276A] transition-all"
      >
        <Sparkles className="w-4 h-4" />
        {t("coach.open")}
        <ChevronDown className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div
      className="mt-6 app-card border-2 border-[#E5276A]/25 overflow-hidden"
      data-testid="coach-ia-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#FCE4ED] to-transparent border-b border-[#E5276A]/12">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#E5276A] text-white flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#1F3A5F]">{t("coach.title")}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#8B95A1]">
              {t(`course.step.${step}`)} · {t("coach.subtitle")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="coach-ia-collapse"
            className="p-1.5 rounded-lg hover:bg-white/60 text-[#4A5568]"
            title={t("coach.reduce")}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="max-h-72 overflow-y-auto px-4 py-3 bg-white space-y-3"
        data-testid="coach-ia-messages"
      >
        {turns.length === 0 && (
          <div className="text-xs text-[#8B95A1] italic">
            {t("coach.hint")}
          </div>
        )}
        {turns.map((turn, i) => (
          <div key={i} className="space-y-1.5" data-testid={`coach-turn-${i}`}>
            <div className="flex justify-end">
              <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-br-sm bg-[#1F3A5F] text-white text-sm">
                {turn.q}
              </div>
            </div>
            <div className="flex justify-start">
              <div className={`max-w-[85%] px-3 py-2 rounded-2xl rounded-bl-sm text-sm ${
                turn.error ? "bg-red-50 text-red-800 border border-red-200" :
                turn.pending ? "bg-[#FBF7F3] text-[#8B95A1]" :
                "bg-[#FBF7F3] text-[#1F3A5F]"
              }`}>
                {turn.pending ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("coach.thinking")}
                  </span>
                ) : turn.error ? (
                  t("coach.retry")
                ) : (
                  <span style={{ whiteSpace: "pre-wrap" }}>{turn.a}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {turns.length === 0 && suggestions.length > 0 && (
        <div className="px-4 pb-2 pt-1 flex flex-wrap gap-1.5" data-testid="coach-suggestions">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setQ(s)}
              className="text-[11px] px-2.5 py-1 rounded-full bg-[#FBF7F3] text-[#1F3A5F] hover:bg-[#FCE4ED] hover:text-[#E5276A] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={send}
        className="flex items-center gap-2 px-3 py-2.5 border-t border-[#1F3A5F]/8 bg-white"
      >
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          maxLength={500}
          placeholder={loggedIn ? t("coach.placeholder.auth") : t("coach.placeholder.anon")}
          data-testid="coach-ia-input"
          className="flex-1 px-3 py-2 text-sm rounded-xl border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:outline-none focus:ring-2 focus:ring-[#E5276A]/15"
        />
        <button
          type="submit"
          disabled={!q.trim() || sending}
          data-testid="coach-ia-send"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-50"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}

function suggestionsFor(step, t) {
  const keys = {
    mission: ["coach.sug.mission.a", "coach.sug.mission.b"],
    explorer: ["coach.sug.explorer.a", "coach.sug.explorer.b"],
    comprendre: ["coach.sug.comprendre.a", "coach.sug.comprendre.b"],
    train: ["coach.sug.train.a", "coach.sug.train.b"],
    challenge: ["coach.sug.challenge.a", "coach.sug.challenge.b"],
    validation: ["coach.sug.validation.a", "coach.sug.validation.b"],
  }[step] || [];
  return keys.map((k) => t(k));
}
